import React, { useState } from 'react';
import api from '../utils/api';
import { formatDateTime, formatDuration } from '../utils/formatters';
import InfoTip from './InfoTip';

// One wording per timing segment, reused by every surface that shows it so
// the table header, the averages and the detail panel can't drift apart.
//
// Every one of these is per-BREAK, never per-LIVE: one TikTok LIVE contains
// several breaks, and real LIVE airtime lives on the Live Sessions page.
const TIMELINE_SCOPE =
  'Per-break, NOT per-LIVE. One TikTok LIVE usually contains several breaks.';

export const FILL_TIME_INFO =
  'FILL TIME — how long this break took to sell out: from the first spot being '
  + 'filled (Break Starts) to the last spot selling (Fill Ends). | ' + TIMELINE_SCOPE
  + ' | Does NOT include rip time. | Breaks filled in a single pass, or taking over '
  + '12 hours, show as -- and are left out of the average.';

const FILL_TO_RIP_INFO =
  'FILL TO RIP DEAD TIME — the gap between the last spot selling and the first card '
  + 'being logged in Card(s). Pure overhead: the break is sold out but nothing is '
  + 'being opened yet. | Nothing is ripped until the board is full, so a card logged '
  + 'while spots were still open does not start the rip — it is ignored, and the rip '
  + 'starts at the first card logged after the board filled.';

const RIP_TIME_INFO =
  'RIP & RECAP — from the first card logged in Card(s) (Rip Starts) to the breaker '
  + 'pressing End Break after the recap (Rip Ends). | The recap is counted inside '
  + 'this segment. | A break where no cards were ever logged has no measurable rip '
  + 'and shows as --.';

export const BREAK_TOTAL_INFO =
  'BREAK TOTAL — the whole break end to end: first spot filled to End Break pressed. '
  + 'Fill + Fill-to-Rip + Rip & Recap. | ' + TIMELINE_SCOPE
  + ' | Shows as -- until End Break is pressed.';

const BETWEEN_BREAKS_INFO =
  'BETWEEN BREAKS DEAD TIME — the gap from this break ending to the next one starting. '
  + '| Only counted when you go directly into the next break: a gap over 30 minutes is '
  + 'treated as a separate session (dinner, or the next day) and shows as --, not as '
  + 'dead time. | Needs End Break to have been pressed on this break.';

// Break Timeline — the four stamps and the three spans between them.
//
// Vertical, because the horizontal version couldn't hold: four stage boxes and
// three connectors as siblings in one wrapping row overflowed the summary panel
// and broke at arbitrary points, stranding a duration under an unrelated stage.
// A fixed-width rail can't wrap, time reads top to bottom the way it runs, and
// each duration sits physically between the two stamps it spans.
//
// The rail itself carries the productive/dead distinction — the same encoding
// as the Break Cycle chart on Insights — so the duration text stays in plain
// ink rather than wearing a series colour.
const TIMELINE_STAGES = [
  { key: 'break_started_at', label: 'Break Starts', hint: 'first spot filled' },
  { key: 'fill_ended_at',    label: 'Fill Ends',    hint: 'last spot sold' },
  { key: 'rip_started_at',   label: 'Rip Starts',   hint: 'first card logged after the board filled' },
  { key: 'break_ended_at',   label: 'Break Ends',   hint: 'End Break pressed' },
];

const TIMELINE_SEGMENTS = [
  { label: 'Fill',        field: 'fill_seconds',        color: '#3987e5', dead: false, info: FILL_TIME_INFO },
  { label: 'Fill → Rip',  field: 'fill_to_rip_seconds', color: '#eb6834', dead: true,  info: FILL_TO_RIP_INFO },
  { label: 'Rip & Recap', field: 'rip_seconds',         color: '#1c5cab', dead: false, info: RIP_TIME_INFO },
];

const RAIL_W = 18;      // marker column
const DOT_TOP = 5;      // where the dot sits inside its row
const DOT = 9;

// 'YYYY-MM-DDTHH:MM:SS' <-> what datetime-local wants. The stamps are stored as
// naive local time, so this is a straight slice — no timezone maths, which
// would shift every stamp by the viewer's offset.
const stampToInput = (s) => (s ? String(s).slice(0, 16) : '');
const inputToStamp = (v) => (v ? `${v}:00` : null);

export default function BreakTimeline({ brk, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function startEditing() {
    setDraft(Object.fromEntries(TIMELINE_STAGES.map(s => [s.key, stampToInput(brk[s.key])])));
    setError('');
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      const payload = Object.fromEntries(
        TIMELINE_STAGES.map(s => [s.key, inputToStamp(draft[s.key])])
      );
      const { data } = await api.put(`/api/breaks/${brk.id}/timing`, payload);
      onSaved(data.break);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save timing');
    } finally {
      setSaving(false);
    }
  }

  // A rip start recorded before the board filled is not a rip start, so the
  // stage reads as unrecorded rather than showing a stamp the figures ignore.
  const ripStampValid = !!brk.rip_started_at && !!brk.fill_ended_at
    && Date.parse(brk.rip_started_at) >= Date.parse(brk.fill_ended_at);
  const stampFor = (key) => (key === 'rip_started_at' && !ripStampValid ? null : brk[key]);

  const rail = (topColor, bottomColor, dotFilled) => (
    <div style={{ position: 'relative', width: RAIL_W, alignSelf: 'stretch' }}>
      {topColor && (
        <div style={{
          position: 'absolute', left: (DOT - 2) / 2, top: 0, height: DOT_TOP + DOT / 2,
          width: 2, background: topColor.color, opacity: topColor.faded ? 0.3 : 1,
        }} />
      )}
      {bottomColor && (
        <div style={{
          position: 'absolute', left: (DOT - 2) / 2, top: DOT_TOP + DOT / 2, bottom: 0,
          width: 2, background: bottomColor.color, opacity: bottomColor.faded ? 0.3 : 1,
        }} />
      )}
      {/* Drawn last so it caps the rail rather than being crossed by it. */}
      <div style={{
        position: 'absolute', left: 0, top: DOT_TOP, width: DOT, height: DOT,
        borderRadius: '50%', boxSizing: 'border-box',
        background: dotFilled ? 'var(--text)' : 'var(--bg-card, #fff)',
        border: dotFilled ? '2px solid var(--text)' : '2px solid var(--border)',
      }} />
    </div>
  );

  const railFull = (seg) => (
    <div style={{ position: 'relative', width: RAIL_W, alignSelf: 'stretch' }}>
      <div style={{
        position: 'absolute', left: (DOT - 2) / 2, top: 0, bottom: 0, width: 2,
        background: seg.color, opacity: seg.faded ? 0.3 : 1,
      }} />
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <label style={{ margin: 0 }}>
          Break Timeline
          <InfoTip
            text={'The four stamps of a break and the spans between them. | Break Starts (first spot filled) '
              + '→ Fill Ends (last spot sold) → Rip Starts (first card marked after the board filled) '
              + '→ Break Ends (End Break pressed). | ' + TIMELINE_SCOPE}
            label="Break Timeline" />
        </label>
        {canEdit && !editing && (
          <button className="btn btn-secondary btn-sm" onClick={startEditing}>Edit times</button>
        )}
        {editing && (
          <>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {TIMELINE_STAGES.map((stage, i) => {
          const stamp = stampFor(stage.key);
          const before = i > 0 ? TIMELINE_SEGMENTS[i - 1] : null;
          const after = i < TIMELINE_SEGMENTS.length ? TIMELINE_SEGMENTS[i] : null;
          const afterMeasured = after && brk[after.field] != null;
          const beforeMeasured = before && brk[before.field] != null;

          return (
            <React.Fragment key={stage.key}>
              {/* Stage: dot, name, stamp */}
              <div style={{ display: 'flex', alignItems: 'flex-start', minHeight: 20 }}>
                {rail(
                  before ? { color: before.color, faded: !beforeMeasured } : null,
                  after ? { color: after.color, faded: !afterMeasured } : null,
                  !!stamp
                )}
                <div style={{
                  flex: 1, display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', gap: 10, flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{stage.label}</span>
                  {editing ? (
                    <input type="datetime-local" value={draft[stage.key] || ''}
                      onChange={e => setDraft(d => ({ ...d, [stage.key]: e.target.value }))}
                      style={{ width: 'auto', fontSize: 12, padding: '2px 6px' }} />
                  ) : (
                    <span style={{
                      fontSize: 13, fontVariantNumeric: 'tabular-nums',
                      color: stamp ? 'var(--text-secondary)' : 'var(--text-light)',
                    }}>
                      {stamp
                        ? formatDateTime(stamp)
                        : (stage.key === 'break_ended_at' && brk.is_running ? 'still running' : 'not recorded')}
                    </span>
                  )}
                </div>
              </div>

              {/* Hint under the stage name */}
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                {after ? railFull({ color: after.color, faded: !afterMeasured })
                       : <div style={{ width: RAIL_W }} />}
                <div style={{ fontSize: 11, color: 'var(--text-light)', paddingBottom: after ? 4 : 0 }}>
                  {stage.hint}
                </div>
              </div>

              {/* The span that follows this stage */}
              {after && (
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 26 }}>
                  {railFull({ color: after.color, faded: !afterMeasured })}
                  <div style={{
                    flex: 1, display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', gap: 10, paddingBottom: 4,
                  }}>
                    <span style={{ fontSize: 12, color: 'var(--text-light)' }}>
                      {after.label}
                      {after.dead && (
                        <span style={{
                          marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                          textTransform: 'uppercase', color: '#9c2b2b',
                          background: 'rgba(208,59,59,0.12)', padding: '1px 5px', borderRadius: 3,
                        }}>dead</span>
                      )}
                      <InfoTip text={after.info} label={after.label} />
                    </span>
                    <span style={{
                      fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      color: afterMeasured ? 'var(--text)' : 'var(--text-light)',
                    }}>
                      {afterMeasured ? formatDuration(brk[after.field]) : '--'}
                    </span>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Totals — outside the rail, because neither one is a span between two
          adjacent stamps: the total brackets the whole thing, and the gap
          belongs to the next break. */}
      <div style={{
        display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 12, paddingTop: 10,
        borderTop: '1px solid var(--border)', fontSize: 12,
      }}>
        <span>
          <span style={{ color: 'var(--text-light)' }}>Break Total</span>{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDuration(brk.break_total_seconds)}</strong>
          <InfoTip text={BREAK_TOTAL_INFO} label="Break Total" />
        </span>
        <span>
          <span style={{ color: 'var(--text-light)' }}>Between Breaks</span>{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDuration(brk.between_breaks_seconds)}</strong>
          <InfoTip text={BETWEEN_BREAKS_INFO} label="Between Breaks" />
        </span>
        {brk.break_ended_source && (
          <span style={{ color: 'var(--text-light)' }}>
            End recorded by: {brk.break_ended_source}
          </span>
        )}
      </div>
    </div>
  );
}
